'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  faMagnifyingGlass,
  faHashtag,
  faArrowDownWideShort,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { Section, Chip, ChipTitle, ChipIcon } from '@systhemaui/next'
import TitleCard from './TitleCard'

function constructFaSvg(icon: IconDefinition) {
  if (!icon || !icon.icon || !icon.icon[0] || !icon.icon[1] || !icon.icon[4]) {
    return ''
  }
  const viewBox = `${icon.icon[0]} ${icon.icon[1]}`
  const path = icon.icon[4]
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewBox}" width="1em" height="1em" fill="currentColor"><path d="${path}"/></svg>`
}

interface TitleItem {
  id: number
  title: string
  slug: string
  imageUrl: string
  tags: Array<{ slug: string; label: string }>
  creators: Array<{ slug: string; label: string }>
  createdAt: string
}

interface TagItem {
  slug: string
  label: string
}

interface ActiveFilter {
  type: 'tag' | 'search' | 'creator'
  slug: string
  label: string
}

interface LibraryClientProps {
  titles: TitleItem[]
  tags: TagItem[]
}

const sortOptions = [
  { value: 'legujabb', label: 'Legfrissebb' },
  { value: 'legregibb', label: 'Legrégebbi' },
]

const DEBOUNCE_DELAY = 500 // ms

export default function LibraryClient({ titles, tags }: LibraryClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Read filter state from URL
  const tagParam = searchParams.get('cimke')
  const tagSlugs = useMemo(() => (tagParam ? tagParam.split(',').filter(Boolean) : []), [tagParam])
  const creatorParam = searchParams.get('kreator')
  const creatorSlugs = useMemo(
    () => (creatorParam ? creatorParam.split(',').filter(Boolean) : []),
    [creatorParam],
  )
  const currentSort = searchParams.get('rendezes') || 'legujabb'
  const currentSearch = searchParams.get('search') || ''

  const [searchValue, setSearchValue] = useState(currentSearch)

  // Sync search input with URL when it changes externally (e.g. chip removal)
  useEffect(() => {
    setSearchValue(currentSearch)
  }, [currentSearch])

  // Client-side filtering
  const filteredTitles = useMemo(() => {
    let result = titles

    // Tag filter
    if (tagSlugs.length > 0) {
      result = result.filter((t) => t.tags.some((tag) => tagSlugs.includes(tag.slug)))
    }

    // Creator filter
    if (creatorSlugs.length > 0) {
      result = result.filter((t) => t.creators.some((c) => creatorSlugs.includes(c.slug)))
    }

    // Search filter
    if (currentSearch) {
      const searchLower = currentSearch.toLowerCase()
      result = result.filter((t) => t.title.toLowerCase().includes(searchLower))
    }

    // Sort
    if (currentSort === 'legregibb') {
      result = [...result].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
    }
    // Default sort is 'legujabb' which matches the server's -createdAt order

    return result
  }, [titles, tagSlugs, creatorSlugs, currentSearch, currentSort])

  const updateFilters = useCallback(
    (newParams: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString())

      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === '') {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })

      const queryString = params.toString()
      router.push(queryString ? `?${queryString}` : '/videotar', { scroll: false })
    },
    [router, searchParams],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchValue(value)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = setTimeout(() => {
        updateFilters({ search: value || null })
      }, DEBOUNCE_DELAY)
    },
    [updateFilters],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTagSlug = e.target.value
    if (!selectedTagSlug) return

    // Check if tag is already selected
    if (tagSlugs.includes(selectedTagSlug)) {
      e.target.value = ''
      return
    }

    const newTags = [...tagSlugs, selectedTagSlug].join(',')
    updateFilters({ cimke: newTags })

    // Reset the select
    e.target.value = ''
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFilters({ rendezes: e.target.value === 'legujabb' ? null : e.target.value })
  }

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    updateFilters({ search: searchValue || null })
  }

  // Build active filter chips
  const activeTagFilters = tagSlugs.reduce<ActiveFilter[]>((acc, slug) => {
    const tag = tags.find((t) => t.slug === slug)
    if (tag) acc.push({ type: 'tag', slug, label: tag.label })
    return acc
  }, [])

  const activeCreatorFilters = creatorSlugs.map<ActiveFilter>((slug) => {
    for (const title of titles) {
      const creator = title.creators.find((c) => c.slug === slug)
      if (creator) return { type: 'creator', slug, label: creator.label }
    }
    return { type: 'creator', slug, label: slug }
  })

  const removeFilter = (filter: ActiveFilter) => {
    if (filter.type === 'search') {
      setSearchValue('')
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      updateFilters({ search: null })
      return
    }

    if (filter.type === 'creator') {
      const newCreators = creatorSlugs.filter((slug) => slug !== filter.slug)
      updateFilters({ kreator: newCreators.length > 0 ? newCreators.join(',') : null })
      return
    }

    // Tag filter
    const newTags = tagSlugs.filter((slug) => slug !== filter.slug)
    updateFilters({ cimke: newTags.length > 0 ? newTags.join(',') : null })
  }

  const displayedFilters: ActiveFilter[] = [
    ...(searchValue ? [{ type: 'search' as const, slug: searchValue, label: searchValue }] : []),
    ...activeTagFilters,
    ...activeCreatorFilters,
  ]

  return (
    <>
      {/* Filters Section */}
      <Section theme="default" layoutBackground="base" className="mt-0! pt-0! pb-0!">
        {/* Filter Row */}
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {/* Search Filter */}
            <form onSubmit={handleSearchSubmit}>
              <div className="library-filter">
                <input
                  type="text"
                  name="search"
                  placeholder="Keresés..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="library-filter-input"
                />
                <button
                  type="submit"
                  className="library-filter-icon"
                  dangerouslySetInnerHTML={{ __html: constructFaSvg(faMagnifyingGlass) }}
                />
              </div>
            </form>

            {/* Tag/Category Filter */}
            <div className="library-filter">
              <select onChange={handleTagChange} defaultValue="" className="library-filter-select">
                <option value="" disabled>
                  Kategória
                </option>
                {tags.map((tag) => (
                  <option key={tag.slug} value={tag.slug}>
                    {tag.label}
                  </option>
                ))}
              </select>
              <span
                className="library-filter-icon"
                dangerouslySetInnerHTML={{ __html: constructFaSvg(faHashtag) }}
              />
            </div>

            {/* Sort Filter */}
            <div className="library-filter">
              <select
                value={currentSort}
                onChange={handleSortChange}
                className="library-filter-select"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span
                className="library-filter-icon"
                dangerouslySetInnerHTML={{ __html: constructFaSvg(faArrowDownWideShort) }}
              />
            </div>
          </div>

          {/* Active Filter Chips */}
          {displayedFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-5">
              {displayedFilters.map((filter) => (
                <Chip
                  as="button"
                  key={`${filter.type}-${filter.slug}`}
                  onClick={() => removeFilter(filter)}
                  className="library-chip"
                >
                  <ChipTitle>{filter.label}</ChipTitle>
                  <ChipIcon
                    className="library-chip-icon"
                    dangerouslySetInnerHTML={{ __html: constructFaSvg(faXmark) }}
                  />
                </Chip>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Title Cards Grid */}
      <Section theme="default" layoutBackground="base" className="pt-section! mt-0!">
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {filteredTitles.map((title) => (
            <TitleCard
              key={title.id}
              title={title.title}
              slug={title.slug}
              imageUrl={title.imageUrl}
            />
          ))}
        </div>

        {filteredTitles.length === 0 && (
          <p className="text-h3 color-heading text-center">Nincs találat a megadott szűrőkkel.</p>
        )}
      </Section>
    </>
  )
}
